/**
 * useShellMacros - attack macros: a saved way of attacking, per profile.
 *
 * PERSISTED (v8.327.0) - they died on every reload before, which no "save
 * this" feature may do. `useMacroStore` owns the stored record and is called
 * first thing, where the shell always kept it; `useShellMacros` holds the
 * verbs. A macro remembers the WEAPON, not the plate it sat on (slotIndex is
 * kept for macros saved before v8.191.0 and as the fallback). The app's own
 * dialog names a macro - window.prompt left in v8.231.0.
 *
 * Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { EMPTY_TOGGLES } from '../engine/actions/toggleEntries';
import { EMPTY_WEAPON_OVERRIDES } from '../engine/weaponOverrides';
import { macroWeaponIndex } from '../engine/roster/rack';
import { loadMacros, saveMacros } from '../utils/macroStore';

/** The stored macros ({ [profileName]: MacroPreset[] }), written back on every change. */
export function useMacroStore() {
  const [attackMacros, setAttackMacros] = useState(() => loadMacros());
  useEffect(() => {
    saveMacros(attackMacros);
  }, [attackMacros]);
  return [attackMacros, setAttackMacros];
}

/** A macro of the attack as it is set up now. */
export function macroOf(name, a) {
  return {
    id: 'macro-' + Date.now(),
    name,
    weaponId: a.currentSlots[a.selectedSlot]?.weaponId,
    slotIndex: a.selectedSlot,
    fireMode: a.fireMode,
    attackToggles: { ...a.attackToggles },
    attackCustomMods: [...a.attackCustomMods],
    hatred: a.hatred,
    rerollDamage: a.rerollDamage,
    targetLocation: a.targetLocation,
    rangeBand: a.rangeBand,
    weaponOverrides: { ...a.weaponOverrides, specials: [...a.weaponOverrides.specials] },
  };
}

/**
 * Set the attack up as a macro says. Old macros carry `freeAttack` (the FREE
 * button went in v8.218.0: ignored), labels under `name` (pre-v8.221.0), and
 * no or partial overrides (pre-v6.21.0 / v6.25.0): spread over EMPTY.
 */
export function applyMacro(macro, s) {
  s.setSelectedSlot(macroWeaponIndex(macro, s.currentSlots));
  s.setFireMode(macro.fireMode);
  s.setAttackToggles({ ...EMPTY_TOGGLES, ...macro.attackToggles });
  s.setAttackCustomMods((macro.attackCustomMods ?? []).map((m) => ({ ...m, label: m.label ?? m.name ?? '' })));
  s.setHatred(macro.hatred);
  s.setRerollDamage(macro.rerollDamage ?? false);
  s.setTargetLocation(macro.targetLocation);
  s.setRangeBand(macro.rangeBand);
  s.setWeaponOverrides({ ...EMPTY_WEAPON_OVERRIDES, ...(macro.weaponOverrides ?? {}) });
}

/** One more macro on this profile's list. */
const withMacro = (prev, profile, macro) => ({ ...prev, [profile]: [...(prev[profile] || []), macro] });

/**
 * Save the attack as set up now. Over the length target on purpose: a
 * binding list and a dependency list, both declarative. It once read
 * `currentSlots` without depending on it (fixed v8.377.3).
 */
function useSaveMacro(a, setAttackMacros) {
  const { selectedSlot, fireMode, attackToggles, attackCustomMods, weaponOverrides, hatred } = a;
  const { rerollDamage, targetLocation, rangeBand, localCurrentProfile, currentSlots } = a;
  return useCallback(
    (name) => {
      if (selectedSlot === null) return;
      const macro = macroOf(name, {
        currentSlots,
        selectedSlot,
        fireMode,
        attackToggles,
        attackCustomMods,
        hatred,
        rerollDamage,
        targetLocation,
        rangeBand,
        weaponOverrides,
      });
      setAttackMacros((prev) => withMacro(prev, localCurrentProfile, macro));
    },
    [
      currentSlots,
      selectedSlot,
      fireMode,
      attackToggles,
      attackCustomMods,
      weaponOverrides,
      hatred,
      rerollDamage,
      targetLocation,
      rangeBand,
      localCurrentProfile,
      setAttackMacros,
    ],
  );
}

/** Delete one macro from this profile's list. */
function useDeleteMacro(localCurrentProfile, setAttackMacros) {
  return useCallback(
    (id) =>
      setAttackMacros((prev) => ({
        ...prev,
        [localCurrentProfile]: (prev[localCurrentProfile] || []).filter((m) => m.id !== id),
      })),
    [localCurrentProfile, setAttackMacros],
  );
}

/**
 * @param {object} args - the stored macros and setter (from useMacroStore),
 *   the profile, the attack as set up (slot, mode, toggles, custom mods,
 *   overrides, qualities, location, band), showDialogAsync, and every
 *   setter a macro restores. Over the length target: its body is bindings.
 */
export default function useShellMacros(args) {
  const { attackMacros, setAttackMacros, localCurrentProfile, showDialogAsync, currentSlots } = args;
  const currentMacros = useMemo(() => attackMacros[localCurrentProfile] || [], [attackMacros, localCurrentProfile]);
  const handleRequestMacroName = useCallback(() => showDialogAsync('prompt', 'Macro name:', ''), [showDialogAsync]);
  const { setSelectedSlot, setFireMode, setAttackToggles, setAttackCustomMods, setHatred } = args;
  const { setRerollDamage, setTargetLocation, setRangeBand, setWeaponOverrides } = args;
  const handleLoadMacro = useCallback(
    (macro) =>
      applyMacro(macro, {
        currentSlots,
        setSelectedSlot,
        setFireMode,
        setAttackToggles,
        setAttackCustomMods,
        setHatred,
        setRerollDamage,
        setTargetLocation,
        setRangeBand,
        setWeaponOverrides,
      }),
    // Setters from useState are stable; the three routed ones are not, so they are listed (v8.377.3).
    [
      setAttackToggles,
      setAttackCustomMods,
      setWeaponOverrides,
      currentSlots,
      setSelectedSlot,
      setFireMode,
      setHatred,
      setRerollDamage,
      setTargetLocation,
      setRangeBand,
    ],
  );
  return {
    currentMacros,
    handleRequestMacroName,
    handleLoadMacro,
    handleSaveMacro: useSaveMacro(args, setAttackMacros),
    handleDeleteMacro: useDeleteMacro(localCurrentProfile, setAttackMacros),
  };
}
